import React, { useState, useEffect } from 'react';
import { X, Video, UserCheck, Mail, User, Phone, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToastContext } from '../../contexts/ToastContext';
import { bookConsultation } from '../../services/consultationService';
import Button from '../shared/Button';
import Input from '../shared/Input';
import CustomSelect from '../shared/CustomSelect';
import { DatePicker, TimePicker } from './index';

const BookConsultationModal = ({ isOpen, onClose, onSuccess, vendorId, vendorName, serviceId, questions = [] }) => {
  const { user, isAuthenticated } = useAuth();
  const toast = useToastContext();
  const [saving, setSaving] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [responses, setResponses] = useState({});

  // Client contact info (especially for logged-out / guest users)
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');

  useEffect(() => {
    if (user && isAuthenticated) {
      setClientName(user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim());
      setClientEmail(user.email || '');
      setClientPhone(user.phone || '');
    }
  }, [user, isAuthenticated, isOpen]);

  const resetForm = () => {
    setScheduledDate('');
    setScheduledTime('');
    setResponses({});
    if (!isAuthenticated) {
      setClientName('');
      setClientEmail('');
      setClientPhone('');
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleResponseChange = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const renderQuestion = (q, idx) => {
    const id = q.id || `q_${idx}`;
    const value = responses[id] ?? '';

    if (q.type === 'textarea') {
      return (
        <div key={id} className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">
            {q.label} {q.required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={value}
            onChange={(e) => handleResponseChange(id, e.target.value)}
            placeholder={q.placeholder}
            required={q.required}
            rows={3}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
          />
        </div>
      );
    }

    if (q.type === 'select') {
      const selectOptions = (q.options || []).filter(Boolean).map((opt) => ({ value: opt, label: opt }));
      return (
        <CustomSelect
          key={id}
          label={`${q.label}${q.required ? ' *' : ''}`}
          options={selectOptions}
          value={value}
          onChange={(v) => handleResponseChange(id, v)}
          placeholder="Select..."
          required={q.required}
        />
      );
    }

    const inputType = q.type === 'email' ? 'email' : q.type === 'phone' ? 'tel' : q.type === 'date' ? 'date' : q.type === 'time' ? 'time' : 'text';
    return (
      <div key={id} className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700">
          {q.label} {q.required && <span className="text-red-500">*</span>}
        </label>
        <Input
          type={inputType}
          value={value}
          onChange={(e) => handleResponseChange(id, e.target.value)}
          placeholder={q.placeholder}
          required={q.required}
        />
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guest validation
    if (!isAuthenticated) {
      if (!clientName.trim()) {
        toast.error(null, 'Please enter your full name');
        return;
      }
      if (!clientEmail.trim() || !/\S+@\S+\.\S+/.test(clientEmail.trim())) {
        toast.error(null, 'Please enter a valid email address');
        return;
      }
    }

    if (!scheduledDate) {
      toast.error(null, 'Please select a date');
      return;
    }

    const requiredQuestions = questions.filter((q) => q.required);
    const missing = requiredQuestions.filter((q) => {
      const id = q.id || `q_${questions.indexOf(q)}`;
      return !responses[id]?.trim();
    });
    if (missing.length > 0) {
      toast.error(null, 'Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const questionnaireResponses = {};
      questions.forEach((q, idx) => {
        const id = q.id || `q_${idx}`;
        if (responses[id] !== undefined) questionnaireResponses[id] = responses[id];
      });

      await bookConsultation({
        vendor_id: vendorId,
        service_id: serviceId || null,
        scheduled_date: scheduledDate,
        scheduled_time: scheduledTime || null,
        questionnaire_responses: questionnaireResponses,
        client_name: clientName.trim() || (user?.name || ''),
        client_email: clientEmail.trim() || (user?.email || ''),
        client_phone: clientPhone.trim() || (user?.phone || null),
      });

      toast.success(
        'Consultation Requested',
        'Your consultation request has been submitted! The vendor will review it and email you the meeting link.'
      );
      onSuccess?.();
      handleClose();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to submit consultation request';
      toast.error('Booking failed', msg);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-[10002] overflow-y-auto animate-fade-in">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} aria-hidden="true" />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-auto animate-scale-in border border-gray-100 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-5 sm:p-6 border-b border-gray-100 flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-11 h-11 bg-secondary-100 rounded-full flex items-center justify-center">
                <Video className="w-6 h-6 text-secondary-600" />
              </div>
              <div>
                <span className="inline-block px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-1">
                  Video Consultation
                </span>
                <h3 className="text-lg font-semibold text-gray-900">
                  {vendorName ? `Consultation with ${vendorName}` : 'Request a Consultation'}
                </h3>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Body */}
          <div className="p-5 sm:p-6 overflow-y-auto flex-1">
            <form id="book-consultation-form" onSubmit={handleSubmit} className="space-y-4">
              
              {/* Guest / Logged-in Notice & Contact Details */}
              {!isAuthenticated ? (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2 text-xs text-slate-700 font-medium">
                    <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span>No login required. Provide your details so the vendor can send your invite.</span>
                  </div>
                  
                  <div className="space-y-3">
                    <Input
                      label="Your Full Name"
                      type="text"
                      placeholder="e.g. Jane Doe"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      required
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input
                        label="Email Address"
                        type="email"
                        placeholder="jane@example.com"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        required
                      />
                      <Input
                        label="Phone Number"
                        type="tel"
                        placeholder="e.g. +1 (555) 000-0000"
                        value={clientPhone}
                        onChange={(e) => setClientPhone(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-emerald-50 border border-emerald-200/80 rounded-xl p-3 sm:p-4 flex items-center justify-between gap-3 text-xs sm:text-sm text-emerald-950">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 flex-shrink-0">
                      <UserCheck className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-emerald-900 truncate">
                        Booking as {user?.name || user?.email}
                      </p>
                      <p className="text-emerald-700 text-xs truncate">
                        Invite & link will be sent to {user?.email}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Date and Time pickers */}
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <div className="flex-1">
                  <DatePicker
                    value={scheduledDate}
                    onChange={setScheduledDate}
                    placeholder="Select date"
                    allowPastDates={false}
                    minDate={minDate}
                    required
                    label="Preferred date"
                  />
                </div>
                <div className="flex-1">
                  <TimePicker
                    value={scheduledTime}
                    onChange={setScheduledTime}
                    placeholder="Select time"
                    format="24"
                    label="Preferred time (optional)"
                  />
                </div>
              </div>

              {/* Vendor Custom Questions */}
              {questions.length > 0 && (
                <div className="border-t border-gray-200 pt-4 space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Consultation Questionnaire</p>
                    <p className="text-xs text-gray-500">Please answer a few quick questions for the vendor.</p>
                  </div>
                  {questions.map((q, idx) => renderQuestion(q, idx))}
                </div>
              )}
            </form>
          </div>

          {/* Footer */}
          <div className="p-4 sm:p-6 border-t border-gray-100 bg-gray-50/50 flex items-center justify-end gap-2 rounded-b-xl">
            <Button type="button" variant="outline" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="book-consultation-form"
              variant="secondary"
              loading={saving}
              disabled={saving}
            >
              Submit Request
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookConsultationModal;
